import React, { useEffect, useState } from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    Show,
    SimpleShowLayout,
    ReferenceField,
    usePermissions,
    TopToolbar,
    DeleteButton,
    Button,
    useRecordContext,
    useNotify,
    useDataProvider,
    useRefresh,
    Create,
    SimpleForm,
    required,
    CreateButton,
    Pagination,
    ShowButton,
} from 'react-admin';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { API_URL } from '../config';
import { JumpstartTextInput, JumpstartSelectInput, JumpstartSearchInput, JumpstartFormToolbar, JumpstartFileInput } from '../components/JumpstartInputs';
import { useT } from '../i18n/useT';

type PublicationChoice = {
    id: number;
    sku: string;
    title: string;
    display_name: string;
};

// Export files button
const ExportFilesButton = () => {
    const notify = useNotify();
    const t = useT();

    const handleExport = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                notify(t('notifications.authRequired'), { type: 'error' });
                return;
            }

            // Fetch all files
            const response = await fetch(`${API_URL}/files?skip=0&limit=10000`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch files');
            }

            const files = await response.json();

            // Create CSV content
            const csvContent = [
                ['ID', 'Publication ID', 'File Type', 'Filename', 'MIME Type', 'Size (Bytes)', 'Created At'],
                ...files.map((file: any) => [
                    file.id,
                    file.publication_id,
                    file.file_type,
                    file.filename,
                    file.mime_type,
                    file.size || 0,
                    file.created_at,
                ]),
            ]
                .map((row: any[]) => row.map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                .join('\n');

            // Create and download CSV file
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `files_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            notify(t('resources.files.notifications.exportSuccess'), { type: 'success' });
        } catch (error) {
            console.error('Export error:', error);
            notify(t('resources.files.notifications.exportFailed'), { type: 'error' });
        }
    };

    return (
        <Button
            label="actions.exportCsv"
            onClick={handleExport}
            startIcon={<FileDownloadIcon />}
        />
    );
};

// List actions toolbar
const FileListActions = () => {
    const { permissions } = usePermissions();
    return (
        <TopToolbar>
            <ExportFilesButton />
            {permissions === 'admin' && <CreateButton />}
        </TopToolbar>
    );
};

// Download button for files
const DownloadButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const t = useT();

    if (!record) return null;

    const handleDownload = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                notify(t('notifications.authRequired'), { type: 'error' });
                return;
            }

            const response = await fetch(`${API_URL}/files/${record.id}/download`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Download failed');
            }

            // Get the filename from Content-Disposition header or use default
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = record.filename || `file_${record.id}.${record.file_type}`;
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            // Create blob and trigger download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            notify(t('notifications.downloadSuccess'), { type: 'success' });
        } catch (error) {
            console.error('Download error:', error);
            notify(t('notifications.downloadFailed'), { type: 'error' });
        }
    };

    return (
        <Button
            label="actions.download"
            onClick={handleDownload}
            startIcon={<CloudDownloadIcon />}
        />
    );
};

// Empty state with create button
const FileListEmpty = () => {
    const t = useT();
    return (
        <div style={{ padding: '1rem', textAlign: 'center' }}>
            <FileListActions />
            <h3>{t('resources.files.empty.title')}</h3>
            <p>{t('resources.files.empty.hint')}</p>
        </div>
    );
};

// Filter bar – sources match backend query param names
const FileFilters = () => {
    const t = useT();
    return [
        <JumpstartSearchInput key="sku"      source="sku"      placeholder={t('resources.files.filters.skuPlaceholder')}      alwaysOn />,
        <JumpstartSearchInput key="filename" source="filename" placeholder={t('resources.files.filters.filenamePlaceholder')} alwaysOn />,
    ];
};

// List of files
export const FileList = () => {
    const { permissions } = usePermissions();

    return (
        <List
            sort={{ field: 'created_at', order: 'DESC' }}
            filters={FileFilters()}
            actions={<FileListActions />}
            perPage={25}
            pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
            empty={<FileListEmpty />}
        >
            <Datagrid rowClick={false}>
                <TextField source="id" />
                <ReferenceField source="publication_id" reference="publications" link="show">
                    <TextField source="title" />
                </ReferenceField>
                <TextField source="file_type" />
                <TextField source="filename" />
                <FunctionField
                    source="size"
                    render={(record: any) => {
                        if (!record.file_size) return 'N/A';
                        const mb = (record.file_size / 1024 / 1024).toFixed(2);
                        return `${mb} MB`;
                    }}
                />
                <DateField source="created_at" showTime />
                <ShowButton />
                <DownloadButton />
                {permissions === 'admin' && <DeleteButton />}
            </Datagrid>
        </List>
    );
};

// Show file details
const FileShowActions = () => {
    const { permissions } = usePermissions();
    return (
        <TopToolbar>
            <DownloadButton />
            {permissions === 'admin' && <DeleteButton />}
        </TopToolbar>
    );
};

export const FileShow = () => {
    return (
        <Show actions={<FileShowActions />}>
            <SimpleShowLayout>
                <TextField source="id" />
                <ReferenceField source="publication_id" reference="publications" link="show">
                    <TextField source="title" />
                </ReferenceField>
                <TextField source="file_type" />
                <TextField source="filename" />
                <TextField source="mime_type" />
                <FunctionField
                    source="size"
                    render={(record: any) => {
                        if (!record.file_size) return 'N/A';
                        const mb = (record.file_size / 1024 / 1024).toFixed(2);
                        const kb = (record.file_size / 1024).toFixed(2);
                        return record.file_size > 1024 * 1024 ? `${mb} MB` : `${kb} KB`;
                    }}
                />
                <ReferenceField source="uploaded_by" reference="users" link="show">
                    <TextField source="email" />
                </ReferenceField>
                <DateField source="created_at" showTime />
                <DateField source="updated_at" showTime />
            </SimpleShowLayout>
        </Show>
    );
};

// Upload file
export const FileCreate = () => {
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const refresh = useRefresh();
    const t = useT();
    const [uploading, setUploading] = useState(false);
    const [publicationChoices, setPublicationChoices] = useState<PublicationChoice[]>([]);
    const [loadingPublications, setLoadingPublications] = useState(false);

    useEffect(() => {
        let active = true;

        const loadPublications = async () => {
            setLoadingPublications(true);

            try {
                const { data } = await dataProvider.getList('publications', {
                    pagination: { page: 1, perPage: 10000 },
                    sort: { field: 'sku', order: 'ASC' },
                    filter: {},
                });

                if (!active) {
                    return;
                }

                setPublicationChoices(
                    data.map((publication: any) => ({
                        id: publication.id,
                        sku: publication.sku,
                        title: publication.title,
                        display_name: `${publication.sku} - ${publication.title}`,
                    }))
                );
            } catch (error) {
                console.error('Failed to load publications for file upload:', error);

                if (active) {
                    notify(t('resources.files.notifications.publicationsLoadFailed'), { type: 'warning' });
                }
            } finally {
                if (active) {
                    setLoadingPublications(false);
                }
            }
        };

        loadPublications();

        return () => {
            active = false;
        };
    }, [dataProvider, notify, t]);

    const handleSubmit = async (data: any) => {
        if (!data.file || !data.file.rawFile) {
            notify(t('resources.files.notifications.selectFile'), { type: 'error' });
            return;
        }

        const selectedSku = typeof data.sku === 'string' ? data.sku.trim() : '';
        const manualSku = typeof data.manual_sku === 'string' ? data.manual_sku.trim() : '';
        const resolvedSku = manualSku || selectedSku;

        if (!resolvedSku) {
            notify(t('resources.files.notifications.selectPublicationOrSku'), { type: 'error' });
            return;
        }

        if (!data.file_type) {
            notify(t('resources.files.notifications.selectFileType'), { type: 'error' });
            return;
        }

        setUploading(true);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                notify(t('notifications.authRequired'), { type: 'error' });
                return;
            }

            const formData = new FormData();
            formData.append('file', data.file.rawFile);
            formData.append('sku', resolvedSku);
            formData.append('file_type', data.file_type);

            const response = await fetch(`${API_URL}/files/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Upload failed');
            }

            notify(t('resources.files.notifications.uploadSuccess'), { type: 'success' });
            refresh();
            // Don't return the response, just return undefined to clear the form
            return undefined;
        } catch (error: any) {
            notify(`${t('resources.files.notifications.uploadFailedPrefix')}: ${error.message}`, { type: 'error' });
            throw error;
        } finally {
            setUploading(false);
        }
    };

    return (
        <Create title={t('resources.files.create.title')}>
            <SimpleForm onSubmit={handleSubmit} toolbar={<JumpstartFormToolbar saveLabel={uploading ? t('resources.files.create.savingLabel') : t('resources.files.create.saveLabel')} />}>
                <JumpstartSelectInput
                    source="sku"
                    label={t('resources.files.form.publication')}
                    choices={publicationChoices}
                    optionText="display_name"
                    optionValue="sku"
                    disabled={uploading || loadingPublications}
                    fullWidth
                    emptyText={loadingPublications ? t('resources.files.create.loadingPublications') : t('resources.files.create.selectPublicationPlaceholder')}
                    helperText={t('resources.files.helpers.publication')}
                />

                <JumpstartTextInput
                    source="manual_sku"
                    label={t('resources.files.form.exactSku')}
                    disabled={uploading}
                    fullWidth
                    helperText={t('resources.files.helpers.exactSku')}
                />
                
                <JumpstartSelectInput
                    source="file_type"
                    label={t('resources.files.form.fileType')}
                    disabled={uploading}
                    choices={[
                        { id: 'pdf', name: t('resources.files.fileTypes.pdf') },
                        { id: 'epub', name: t('resources.files.fileTypes.epub') },
                    ]}
                    validate={[required()]}
                />
                
                <JumpstartFileInput
                    source="file"
                    label={t('resources.files.form.file')}
                    disabled={uploading}
                    accept=".pdf,.epub"
                    validate={[required()]}
                    buttonText={t('resources.files.form.selectFileButton')}
                    helperText={t('resources.files.helpers.file')}
                />
            </SimpleForm>
        </Create>
    );
};

export const filesIcon = AttachFileIcon;
